/* Overlaps and "✂️ Fit" (owner request 2026-10-15, option D): the forward partner of Magic Time Sync, for entries that already
   overlap. For one person, entry X overlaps when its real end (start instant + duration_seconds; never read from the end clock
   string) is later than the start of that person's NEXT item: the entry with the next later start instant (equal starts: by id,
   so the order is always the same), or the person's running timer when it started after X and before that next entry.
   Fit ends X 1 second before the next item starts (the same 1-second gap as Magic Time Sync). Pure arithmetic only
   (tested in tests/app/overlap.test.ts); every time here is a real instant (ms). */
import {entryStartMs, SYNC_GAP_MS} from './time.ts';

export interface OvEntry {id: string; memberId: string; startedAt: string; so: number; durationSeconds: number}
export interface OvTimer {memberId: string; startedAt: string}
export interface Overlap<E extends OvEntry = OvEntry> {
  /** the next item: an entry, or the person's running timer (next = null) */ kind: 'entry' | 'timer'; next: E | null;
  startMs: number; endMs: number; nextStartMs: number;
  /** how far X runs into the next item, in whole seconds (at least 1) */ seconds: number;
  /** Fit is possible: the next item starts after X (Fit never shortens an entry to zero or less) */ fixable: boolean;
  /** Fit's end (the next start − 1 s, to the whole second) and the duration it gives (0 when not fixable) */ fitEndMs: number; fitSeconds: number;
}

const sec = (ms: number) => Math.floor(ms / 1000) * 1000;
/** Fit for an entry starting at startMs whose next item starts at nextStartMs: ends 1 second before it (whole seconds); null when
    that would leave nothing (the next item starts at or before this one, or less than 2 seconds after it) */
export function fitEnd(startMs: number, nextStartMs: number): {endMs: number; durationSeconds: number} | null {
  const endMs = sec(nextStartMs) - SYNC_GAP_MS, d = Math.round((endMs - startMs) / 1000);
  return d >= 1 ? {endMs, durationSeconds: d} : null;
}

/** every overlapping entry of every person in the list, by entry id. Sorted once per person (thousands of entries are fine). */
export function findOverlaps<E extends OvEntry>(entries: E[], timers: OvTimer[] = []): Map<string, Overlap<E>> {
  const byMember = new Map<string, {e: E; s: number}[]>();
  for (const e of entries) {
    const s = entryStartMs(e.startedAt, e.so); if (isNaN(s)) continue;
    const l = byMember.get(e.memberId); l ? l.push({e, s}) : byMember.set(e.memberId, [{e, s}]);
  }
  const timerAt = new Map<string, number>();
  for (const t of timers) { const ms = Date.parse(t.startedAt); if (!isNaN(ms)) timerAt.set(t.memberId, ms); }
  const out = new Map<string, Overlap<E>>();
  for (const [memberId, list] of byMember) {
    list.sort((a, b) => a.s - b.s || (a.e.id < b.e.id ? -1 : a.e.id > b.e.id ? 1 : 0));
    const tms = timerAt.get(memberId);
    for (let i = 0; i < list.length; i++) {
      const {e, s} = list[i], nx = list[i + 1];
      let kind: 'entry' | 'timer' = 'entry', next: E | null = nx ? nx.e : null, ns = nx ? nx.s : NaN;
      if (tms !== undefined && tms > s && (!nx || tms < nx.s)) { kind = 'timer'; next = null; ns = tms; }
      if (isNaN(ns)) continue;
      const end = s + (e.durationSeconds || 0) * 1000;
      if (!(end > ns)) continue;
      const fit = ns > s ? fitEnd(s, ns) : null;
      out.set(e.id, {kind, next, startMs: s, endMs: end, nextStartMs: ns, seconds: Math.max(1, Math.round((end - ns) / 1000)),
                     fixable: !!fit, fitEndMs: fit ? fit.endMs : NaN, fitSeconds: fit ? fit.durationSeconds : 0});
    }
  }
  return out;
}
/** one entry's overlap (the Edit window): only this person's entries are looked at */
export function overlapOf<E extends OvEntry>(entries: E[], timers: OvTimer[], entry: E): Overlap<E> | null {
  return findOverlaps(entries.filter(e => e.memberId === entry.memberId && e.id !== entry.id).concat([entry]), timers.filter(t => t.memberId === entry.memberId)).get(entry.id) || null;
}
/** Lists hide overlaps shorter than this (owner decision 2026-10-15): under 1 minute gets no red mark on any entry list (Entries
    page, every home style, the Groups folded line). The Edit window still shows any overlap, however small, with "✂️ Fit". */
export const LIST_MIN_OVERLAP_SECONDS = 60;
/** the lists mark this overlap (it runs at least LIST_MIN_OVERLAP_SECONDS into the next item; real instants, not the rounded amount) */
export const shownInList = (o: Overlap<any>): boolean => o.endMs - o.nextStartMs >= LIST_MIN_OVERLAP_SECONDS * 1000;
/** "12 s", "20 min", "1 h 5 min" (rounded to the minute from 1 minute on) */
export function overlapAmount(seconds: number): string {
  if (seconds < 60) return seconds + ' s';
  const m = Math.round(seconds / 60);
  return m < 60 ? m + ' min' : Math.floor(m / 60) + ' h' + (m % 60 ? ' ' + (m % 60) + ' min' : '');
}
/** the list's red line: "⚠️ Overlaps the next entry by 20 min — tap to fit" (own: the viewer's own entry, "your running timer") */
export function overlapText(o: Overlap<any>, own = true): string {
  return `⚠️ Overlaps ${o.kind === 'timer' ? (own ? 'your' : 'the') + ' running timer' : 'the next entry'} by ${overlapAmount(o.seconds)} — ${o.fixable ? 'tap to fit' : 'tap to change the times'}`;
}
